import { Alert, Box, Card, Chip, Typography, useMediaQuery, useTheme } from "@mui/material";
import DirectionsBusFilledIcon from "@mui/icons-material/DirectionsBusFilled";
import EventSeatIcon from "@mui/icons-material/EventSeat";

const MAX_SEATS = 10;

function groupSeatsIntoRows(seats) {
    const rowMap = new Map();
    for (const seat of seats ?? []) {
        if (!rowMap.has(seat.row)) rowMap.set(seat.row, []);
        rowMap.get(seat.row).push(seat);
    }

    const rowNumbers = Array.from(rowMap.keys()).sort((a, b) => a - b);

    return rowNumbers.map(rowNum => {
        const rowSeats = rowMap.get(rowNum);
        const left = rowSeats.filter(s => s.column <= 2).sort((a, b) => a.column - b.column);
        const right = rowSeats.filter(s => s.column >= 3).sort((a, b) => a.column - b.column);
        return { left, right };
    });
}

// Adjacent seats: same row, same side of aisle, next column
export function getAdjacentSeats(allSeats, targetSeat) {
    if (!targetSeat || !allSeats?.length) return [];

    const sameRow = allSeats.filter(s => s.row === targetSeat.row && s.number !== targetSeat.number);

    const sameSide = sameRow.filter(s => s.column <= 2 === targetSeat.column <= 2);

    return sameSide.filter(s => Math.abs(s.column - targetSeat.column) === 1);
}

// Checks gender conflict with pre-booked neighboring seats
export function getAdjacentGenderConflict(allSeats, targetSeat, gender) {
    if (!gender || !targetSeat) return null;

    const neighbors = getAdjacentSeats(allSeats, targetSeat);

    for (const neighbor of neighbors) {
        if (neighbor.status === "BookedForFemale" && gender.id === "1") return "2";
        if (neighbor.status === "BookedForMale" && gender.id === "2") return "1";
    }

    return null;
}

function LegendItem({ colorSx, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.75, sm: 1 } }}>
            <Box
                sx={{
                    width: { xs: 16, sm: 20 },
                    height: { xs: 16, sm: 20 },
                    borderRadius: 1,
                    flexShrink: 0,
                    ...colorSx,
                    "&:hover": {},
                }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: "0.68rem", sm: "0.75rem" }, whiteSpace: "nowrap" }}>
                {label}
            </Typography>
        </Box>
    );
}

export function SeatMap({ seats, selectedSeats, onToggleSeat, requiredCount }) {
    const theme = useTheme();
    const isVertical = useMediaQuery(theme.breakpoints.down("sm"));

    const isEmptySlot = seat => !seat || seat.number === "-1" || seat.status == null;

    const getSeatStatus = seat => {
        if (isEmptySlot(seat)) return "empty";
        if (seat.status === "BookedForFemale") return "bookedForFemale";
        if (seat.status === "BookedForMale") return "bookedForMale";
        if (selectedSeats.includes(seat.number)) return "selected";
        return "available";
    };

    const statusStyles = {
        available: {
            bgcolor: "background.paper",
            border: "1px solid",
            borderColor: "grey.300",
            color: "text.primary",
            cursor: "pointer",
            "&:hover": { borderColor: "primary.main", bgcolor: "primary.50" },
        },
        selected: {
            bgcolor: "var(--primary-color)",
            color: "primary.contrastText",
            cursor: "pointer",
        },
        bookedForFemale: {
            bgcolor: "var(--pink-color)",
            color: "var(--light-color)",
            cursor: "not-allowed",
        },
        bookedForMale: {
            bgcolor: "var(--blue-color)",
            color: "var(--light-color)",
            cursor: "not-allowed",
        },
        empty: {
            bgcolor: "transparent",
            border: "none",
            cursor: "default",
            visibility: "hidden",
        },
    };

    const handleClick = seat => {
        const status = getSeatStatus(seat);
        if (status === "bookedForFemale" || status === "bookedForMale" || status === "empty") return;

        if (status === "selected") {
            onToggleSeat(seat.number, false);
            return;
        }

        if (selectedSeats.length >= MAX_SEATS) return;
        onToggleSeat(seat.number, true);
    };

    const rows = groupSeatsIntoRows(seats);

    const renderSeatBox = seat => {
        const status = getSeatStatus(seat);

        return (
            <Box
                key={`${seat.row}-${seat.column}`}
                onClick={() => handleClick(seat)}
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    width: { xs: 44, md: 48 },
                    height: { xs: 44, md: 48 },
                    flexShrink: 0,
                    borderRadius: { xs: 1.5, sm: 2 },
                    transition: "all 0.15s",
                    ...statusStyles[status],
                }}
            >
                {status !== "empty" && (
                    <>
                        <EventSeatIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                        <Typography variant="caption" fontWeight="bold" sx={{ lineHeight: 1, fontSize: "0.75rem" }}>
                            {seat.number}
                        </Typography>
                    </>
                )}
            </Box>
        );
    };

    return (
        <Card variant="outlined" sx={{ p: { xs: 1.5, sm: 2.5, md: 3 }, borderRadius: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 3 } }}>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: { xs: 1.5, sm: 2 },
                    flexWrap: "wrap",
                    gap: 1,
                }}
            >
                <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}>
                    انتخاب صندلی
                </Typography>
                <Chip
                    size="small"
                    color={selectedSeats.length === requiredCount ? "success" : "warning"}
                    label={selectedSeats.length > 0 ? `${selectedSeats.length} صندلی انتخاب شده` : "صندلی انتخاب نشده"}
                    sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}
                />
            </Box>

            <Box
                sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: { xs: 1, sm: 2 },
                    mb: { xs: 2, sm: 3 },
                }}
            >
                <LegendItem colorSx={statusStyles.available} label="قابل انتخاب" />
                <LegendItem colorSx={statusStyles.selected} label="انتخاب شما" />
                <LegendItem colorSx={statusStyles.bookedForFemale} label="رزرو شده (خانم)" />
                <LegendItem colorSx={statusStyles.bookedForMale} label="رزرو شده (آقا)" />
            </Box>

            {selectedSeats.length >= MAX_SEATS && (
                <Alert severity="warning" sx={{ mb: 2, fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                    حداکثر {MAX_SEATS} صندلی قابل انتخاب است.
                </Alert>
            )}
            <Box
                sx={{
                    maxWidth: "100%",
                    width: isVertical ? "100%" : "fit-content",
                    overflowX: isVertical ? "visible" : "auto",
                    WebkitOverflowScrolling: "touch",
                    display: "flex",
                    flexDirection: isVertical ? "column" : "row",
                    alignItems: "center",
                    border: "2px solid",
                    borderColor: "grey.300",
                    borderRadius: { xs: 2.5, sm: 4 },
                    p: { xs: 1.25, sm: 2 },
                    bgcolor: "grey.50",
                    mx: "auto",
                    pb: isVertical ? { xs: 1.25 } : { xs: 1.5, sm: 2 },
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: isVertical ? "column" : "row",
                        alignItems: "center",
                        gap: { xs: 1, sm: 1.5 },
                        direction: "rtl",
                        flexShrink: 0,
                        width: isVertical ? "100%" : "auto",
                    }}
                >
                    {rows?.map((row, rowIndex) => (
                        <Box
                            key={rowIndex}
                            sx={{
                                display: "flex",
                                flexDirection: isVertical ? "row" : "column",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: isVertical ? "row" : "column",
                                    gap: { xs: 0.75, sm: 1 },
                                }}
                            >
                                {row.left?.map(seat => renderSeatBox(seat))}
                            </Box>

                            {/* aisle gap */}
                            <Box sx={isVertical ? { width: { xs: 14, sm: 20 } } : { height: { xs: 16, sm: 28 } }} />

                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: isVertical ? "row" : "column",
                                    gap: { xs: 0.75, sm: 1 },
                                }}
                            >
                                {row.right?.map(seat => renderSeatBox(seat))}
                            </Box>
                        </Box>
                    ))}
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.5,
                        flexShrink: 0,
                        order: isVertical ? -1 : 0,
                        ...(isVertical
                            ? {
                                  width: "100%",
                                  mb: 1.25,
                                  pb: 1,
                                  borderBottom: "2px dashed",
                                  borderColor: "grey.300",
                              }
                            : {
                                  ml: { sm: 2.5 },
                                  pl: { sm: 1.5 },
                                  borderLeft: "2px dashed",
                                  borderColor: "grey.300",
                                  writingMode: "sideways-lr",
                              }),
                    }}
                >
                    <DirectionsBusFilledIcon sx={{ color: "grey.500", fontSize: { xs: 18, sm: 24 } }} />
                    <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ fontSize: { xs: "0.62rem", sm: "0.75rem" } }}>
                        جلوی اتوبوس
                    </Typography>
                </Box>
            </Box>
        </Card>
    );
}
